import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { RendererActions } from '@atlaskit/renderer/actions';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { SubscriptionsDispatchType } from '@confluence/graphql/entry-points/subscriptionTypes.ts';
import { eventToAvi } from '@confluence/pubsub-client/entry-points/PubSubClient.ts';
import { getPubSubClient } from '@confluence/pubsub-client/entry-points/client.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { updateRendererAnnotations } from '@confluence/comments-util/entry-points/rendererAnnotations.ts';
import type {
	CommentData,
	ReplyData,
	CommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	CommentActionType,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import type { ResolvedProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import type { UnreadCommentIDSetOperation } from '@confluence/unread-comments/entry-points/unreadCommentsTypes.ts';

import {
	InlineCommentPubSubEvents,
	PagePubSubEvents,
	InlineCommentSubscriptionEvents,
	PageSubscriptionEvents,
} from '../CommentPubSubEvents.ts';
import {
	refreshCommentThread,
	handleAddedReplyInCommentThread,
	handleAddNewCommentThread,
	handleEditedComment,
} from '../utils/pubsubUtils.ts';

type InlineCommentRequestBody = {
	adfBodyContent?: string;
	commentId: string;
	inlineCommentType: 'TOP_LEVEL' | 'REPLY';
	markerRef: string;
	publishVersionNumber?: number;
	step?: {
		to: number;
		from: number;
		pos?: number;
		mark: {
			attrs: {
				annotationType: AnnotationTypes;
			};
		};
	};
	inlineResolveProperties?: {
		resolved: boolean;
		resolvedTime: number;
		resolvedFriendlyDate: string;
		resolvedUser: string;
		isDangling: boolean;
		jsonObjectSerialize: string;
	};
	inlineText?: string;
	replies?: string[];
};

type CommentPublicationEventParams = {
	contentId?: string;
	currentUserId?: string | null;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed';
	rendererActions?: RendererActions;
	updateDocument?: (newAdf: JSONDocNode) => void;
	updateUnresolvedInlineComment?: (unresolvedInlineComments: string[]) => void;
	needsContentUpdate?: boolean;
	setContentNeedsUpdate?: (contentNeedsUpdate: boolean) => void;
	publishedDocumentVersion?: number;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	addNewCommentThreads?: (newCommentThreads: CommentsDataMap) => void;
	addReplyToCommentThread?: (
		parentMarkerRef: string,
		reply: ReplyData,
		commentType: CommentType,
	) => void;
	updateRemovedCommentIdsMap?: (args_0: {
		threadKey: string;
		commentId: string;
		commentActionType: CommentActionType.DELETE_COMMENT | CommentActionType.RESOLVE_COMMENT_THREAD;
		isReply: boolean;
		commentType: CommentType;
		resolvedProperties?: {
			inlineResolveProperties: ResolvedProperties;
			inlineText: string;
		};
	}) => void;
	reopenParentComment?: (args_0: { threadKey: string; commentType: CommentType }) => void;
	updateCommentBody?: (
		commentId: string,
		threadKey: string,
		newBodyValue: string,
		commentType: CommentType,
	) => void;
	storedCommentData?: any;
	setStoredCommentData?: (commentData: any) => void;
	updateUnreadCommentIDSet?: (
		unreadCommentIDSet: Set<string>,
		operation?: UnreadCommentIDSetOperation,
	) => void;
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	deleteCommentInCache?: ({
		commentId,
		onError,
	}: {
		commentId: string;
		onError?: (error: Error) => void;
	}) => void;
	setActiveHighlight?: (markerRef: string | null) => void;
	setOrderedActiveAnnotationIdList?: (newCommentsList: string[]) => void;
	orderedActiveAnnotationIdList?: Array<{ threadKey: string; isLoaded: boolean }>;
};

type PagePublicationEventParams = {
	currentUserId?: string | null;
	setContentNeedsUpdate?: (contentNeedsUpdate: boolean) => void;
	publishedDocumentVersion?: number;
	setPublishedDocumentVersion?: (publishedDocumentVersion: number) => void;
};

export type RendererInlineCommentsPubSubFn = {
	contentId: string;
	currentUserId: string | null;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed';
	rendererActions: RendererActions;
	updateDocument: (newAdf: JSONDocNode) => void;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	setContentNeedsUpdate?: (contentNeedsUpdate: boolean) => void;
	needsContentUpdate: boolean;
	setPublishedDocumentVersion?: (publishedDocumentVersion: number) => void;
	publishedDocumentVersion: number;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	addNewCommentThreads: (newCommentThreads: CommentsDataMap) => void;
	addReplyToCommentThread: (
		parentMarkerRef: string,
		reply: ReplyData,
		commentType: CommentType,
	) => void;
	updateRemovedCommentIdsMap: (args_0: {
		threadKey: string;
		commentId: string;
		commentActionType: CommentActionType.DELETE_COMMENT | CommentActionType.RESOLVE_COMMENT_THREAD;
		isReply: boolean;
		commentType: CommentType;
		resolvedProperties?: {
			inlineResolveProperties: ResolvedProperties;
			inlineText: string;
		};
	}) => void;
	reopenParentComment?: (args_0: { threadKey: string; commentType: CommentType }) => void;
	updateCommentBody?: (
		commentId: string,
		threadKey: string,
		newBodyValue: string,
		commentType: CommentType,
	) => void;
	storedCommentData?: () => CommentData | null;
	setStoredCommentData?: (commentData: any) => void;
	addSubscriber?: SubscriptionsDispatchType['addSubscriber'];
	setContentType?: SubscriptionsDispatchType['setContentType'];
	setContentId?: SubscriptionsDispatchType['setContentId'];
	updateUnreadCommentIDSet?: (
		unreadCommentIDSet: Set<string>,
		operation?: UnreadCommentIDSetOperation,
	) => void;
	addCommentInCache?: ({
		newComment,
		onError,
	}: {
		newComment: any;
		onError?: (error: Error) => void;
	}) => void;
	deleteCommentInCache?: ({
		commentId,
		onError,
	}: {
		commentId: string;
		onError?: (error: Error) => void;
	}) => void;
	setActiveHighlight?: (markerRef: string | null) => void;
	setOrderedActiveAnnotationIdList?: (newCommentsList: string[]) => void;
	orderedActiveAnnotationIdList?: Array<{ threadKey: string; isLoaded: boolean }>;
};

export type RendererInlineCommentsPubUnsubFn = {
	contentId: string;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed';
	removeSubscriber?: SubscriptionsDispatchType['removeSubscriber'];
	subscriptions: Array<{ subscriberId: string; delta: string }>;
};

const sendFailedPubsubEvent = ({
	createAnalyticsEvent,
	publishVersionNumber,
	publishedDocumentVersion,
}: {
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	publishVersionNumber?: number;
	publishedDocumentVersion?: number;
}) => {
	if (createAnalyticsEvent && publishVersionNumber && publishedDocumentVersion) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'rendererInlineCommentsPubSub',
				action: 'failed',
				actionSubject: 'createPubSubEvent',
				actionSubjectId: 'rendererInlineComments',
				attributes: {
					stepVersion: publishVersionNumber,
					documentVersion: publishedDocumentVersion,
				},
			},
		}).fire();
	}
};

const isEventCommentThreadOpen = (markerRef?: string) => {
	if (!markerRef) {
		return false;
	}

	const focusedMarkerRef = document.querySelector("[data-has-focus='true']");

	// Figure out if the reply's parent markerRef is open
	// HACK: Is there a way to do this without query selectors?
	return focusedMarkerRef && focusedMarkerRef.id === markerRef;
};

export const applyStepAndAddCommentToDoc = ({
	inlineCommentRequestBody,
	rendererActions,
	updateDocument,
	needsContentUpdate,
	setContentNeedsUpdate,
	publishedDocumentVersion,
	createAnalyticsEvent,
}: {
	inlineCommentRequestBody: InlineCommentRequestBody;
	rendererActions?: RendererActions;
	updateDocument?: (newAdf: JSONDocNode) => void;
	needsContentUpdate?: boolean;
	setContentNeedsUpdate?: (contentNeedsUpdate: boolean) => void;
	publishedDocumentVersion?: number;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}) => {
	if (createAnalyticsEvent) {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'rendererInlineCommentsPubSub',
				action: 'received',
				actionSubject: 'createPubSubEvent',
				actionSubjectId: 'rendererInlineComments',
			},
		}).fire();
	}

	const { markerRef, step, publishVersionNumber } = inlineCommentRequestBody;

	// If the users content is out of date or different, the steps from this pubsub event will be wrong
	// The new comment flag shown by VQR will be triggered to refetch the content
	if (step && publishVersionNumber === publishedDocumentVersion && !needsContentUpdate) {
		const {
			to,
			from,
			pos,
			mark: {
				attrs: { annotationType },
			},
		} = step;

		const position = pos !== undefined && pos !== null ? { to: pos, from: pos } : { to, from };
		const annotation = { annotationId: markerRef!, annotationType };
		const result = rendererActions?.applyAnnotation(position, annotation);
		if (result) {
			updateDocument && updateDocument(result.doc);
		}
	} else {
		if (!needsContentUpdate) {
			setContentNeedsUpdate && setContentNeedsUpdate(true);
		}

		// If we can't apply the step, send an event
		sendFailedPubsubEvent({
			createAnalyticsEvent,
			publishVersionNumber,
			publishedDocumentVersion,
		});
	}
};

export const applyStepAndRemoveCommentFromDoc = ({
	inlineCommentRequestBody,
	rendererActions,
	updateDocument,
	eventEmitter,
}: {
	inlineCommentRequestBody: InlineCommentRequestBody;
	rendererActions?: RendererActions;
	updateDocument?: (newAdf: JSONDocNode) => void;
	eventEmitter?: AnnotationUpdateEmitter;
}) => {
	const { markerRef } = inlineCommentRequestBody;

	if (isEventCommentThreadOpen(markerRef)) {
		eventEmitter?.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
	}

	const deleteResult = rendererActions?.deleteAnnotation(markerRef, AnnotationTypes.INLINE_COMMENT);

	if (deleteResult) {
		updateDocument && updateDocument(deleteResult.doc);
	}
};

const refreshCommentThreadIfOpen = ({
	contentId,
	inlineCommentRequestBody,
}: {
	contentId?: string;
	inlineCommentRequestBody: InlineCommentRequestBody;
}) => {
	const { markerRef } = inlineCommentRequestBody;

	if (isEventCommentThreadOpen(markerRef) && contentId) {
		// If so, update it's cache entry
		void refreshCommentThread(contentId, markerRef!);
	}

	// Otherwise do nothing
};

let pagePublicationEventParams: PagePublicationEventParams = {};
const setPagePublicationEventParams = (params: PagePublicationEventParams) => {
	pagePublicationEventParams = params;
};
const onPagePublicationEvent = async (event: string, payload: any) => {
	let confVersion;
	let trigger;
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		confVersion = payload.pageUpdated.confVersion;
		trigger = payload.pageUpdated.trigger;
	} else {
		confVersion = payload.pageUpdatedRequestBody.confVersion;
		trigger = payload.pageUpdatedRequestBody.trigger;
	}

	const { setContentNeedsUpdate, publishedDocumentVersion, setPublishedDocumentVersion } =
		pagePublicationEventParams;

	// need to modify this for subscriptions since the event wont have avi/know content type this way
	// myabe the type in the page updated response will be what we want
	// if pageupdated trigger is publish
	if (
		(event === PagePubSubEvents.PagePublished ||
			event === PagePubSubEvents.BlogPublished ||
			(event === PageSubscriptionEvents.PageUpdated && trigger === 'publish')) &&
		publishedDocumentVersion !== confVersion
	) {
		// TODO: Eventually we want to read the values passed to us to see if this is positional breaking
		setContentNeedsUpdate && setContentNeedsUpdate(true);
		if (expValEquals('confluence_fe_disable_comment_if_page_outdated_v2', 'isEnabled', true)) {
			setPublishedDocumentVersion && setPublishedDocumentVersion(confVersion);
		}
	}
};

let commentPublicationEventParams: CommentPublicationEventParams = {};
export const setCommentPublicationEventParams = (params: CommentPublicationEventParams) => {
	commentPublicationEventParams = params;
};
export const onCommentPublicationEvent = async (event: string, payload: any) => {
	const eventEmitter: AnnotationUpdateEmitter = getRendererAnnotationEventEmitter();
	const { accountId } = payload;
	let inlineCommentRequestBody;
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		inlineCommentRequestBody = payload?.[event];
	} else {
		inlineCommentRequestBody = payload.inlineCommentRequestBody;
	}
	const { inlineCommentType, markerRef, commentId } = inlineCommentRequestBody || {};
	const {
		contentId,
		currentUserId,
		rendererActions,
		updateDocument,
		updateUnresolvedInlineComment,
		needsContentUpdate,
		setContentNeedsUpdate,
		publishedDocumentVersion,
		createAnalyticsEvent,
		addNewCommentThreads,
		addReplyToCommentThread,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		updateCommentBody,
		storedCommentData,
		setStoredCommentData,
		updateUnreadCommentIDSet,
		addCommentInCache,
		deleteCommentInCache,
		setActiveHighlight,
		setOrderedActiveAnnotationIdList,
		orderedActiveAnnotationIdList,
	} = commentPublicationEventParams;

	// Don't do anything when the current user performs an action
	if (accountId === currentUserId) {
		return;
	}

	const lastestStoredCommentData = storedCommentData?.();

	const isReply = inlineCommentType === 'REPLY';

	switch (event) {
		case InlineCommentSubscriptionEvents.InlineCommentUnresolved:
		case InlineCommentPubSubEvents.InlineCommentUnresolved:
			if (event) {
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					if (commentId && contentId) {
						// fetchCommentById implicitly updates useAllCommentsDataQuery cache
						await fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
					}
				} else {
					// We load resolved + active separately, so unless the user has visited the resolved tab, the comment thread won't be in the state
					// Hance the await here - as part of this function we update state
					await handleAddNewCommentThread({
						contentId,
						markerRef,
						addNewCommentThreads,
					});
				}
				// Remove from commentThreadsRemovedByOtherUsers when reopened
				if (markerRef && reopenParentComment) {
					reopenParentComment({
						threadKey: markerRef,
						commentType: CommentType.INLINE,
					});
				}
			}
			eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
				[markerRef!]: {
					id: markerRef,
					annotationType: AnnotationTypes.INLINE_COMMENT,
					state: AnnotationMarkStates.ACTIVE,
				},
			});
			break;
		case InlineCommentSubscriptionEvents.InlineCommentResolved:
		case InlineCommentPubSubEvents.InlineCommentResolved:
			// If the thread is open, close it and clear highlight
			if (isEventCommentThreadOpen(markerRef)) {
				eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
				if (expValEquals('confluence_comment_resolve_kbs_fix', 'isEnabled', true)) {
					setActiveHighlight?.(null);
				}
			}

			// Remove the resolved comment from the navigation list
			if (
				setOrderedActiveAnnotationIdList &&
				orderedActiveAnnotationIdList &&
				expValEquals('confluence_comment_resolve_kbs_fix', 'isEnabled', true)
			) {
				setOrderedActiveAnnotationIdList(
					orderedActiveAnnotationIdList
						.map((status) => status.threadKey)
						.filter((id) => id !== markerRef),
				);
			}

			eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
				[markerRef!]: {
					id: markerRef,
					annotationType: AnnotationTypes.INLINE_COMMENT,
					state: AnnotationMarkStates.RESOLVED,
				},
			});

			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				if (commentId && contentId) {
					// fetchCommentById implicitly updates useAllCommentsDataQuery cache
					void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
				}
			}
			const { inlineResolveProperties, inlineText } = inlineCommentRequestBody;

			updateRemovedCommentIdsMap &&
				inlineResolveProperties &&
				inlineText &&
				updateRemovedCommentIdsMap({
					threadKey: markerRef,
					commentId,
					commentActionType: CommentActionType.RESOLVE_COMMENT_THREAD,
					isReply,
					commentType: CommentType.INLINE,
					resolvedProperties: {
						inlineResolveProperties,
						inlineText,
					},
				});

			const { replies = [] } = inlineCommentRequestBody;

			if (updateUnreadCommentIDSet) {
				updateUnreadCommentIDSet(new Set([commentId, ...replies]), 'remove');
			}

			break;
		case InlineCommentSubscriptionEvents.RendererInlineCommentCreated:
		case InlineCommentPubSubEvents.RendererInlineCommentCreated:
			if (inlineCommentType === 'TOP_LEVEL') {
				// New comments data manger needs to call this AFTER update. See note below
				if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					applyStepAndAddCommentToDoc({
						inlineCommentRequestBody,
						rendererActions,
						updateDocument,
						needsContentUpdate,
						setContentNeedsUpdate,
						publishedDocumentVersion,
						createAnalyticsEvent,
					});
				}

				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					if (addCommentInCache && commentId && contentId) {
						const comment = await fetchCommentById({
							commentId,
							contentId,
							fetchPolicy: 'network-only',
						});
						if (comment) {
							addCommentInCache({ newComment: comment });
						}

						// This needs to happen AFTER the cache update. applyStepAndAddCommentToDoc triggers the doc to re-render, which triggers getRendererAnnotationStates
						// getRendererAnnotationStates pulls data from the cache to determine which annotations to show
						// In the legacy version this wasn't a problem, since it does a full network call
						applyStepAndAddCommentToDoc({
							inlineCommentRequestBody,
							rendererActions,
							updateDocument,
							needsContentUpdate,
							setContentNeedsUpdate,
							publishedDocumentVersion,
							createAnalyticsEvent,
						});
					}
				} else {
					void handleAddNewCommentThread({
						contentId,
						markerRef,
						addNewCommentThreads,
					});
				}

				if (updateUnreadCommentIDSet) {
					updateUnreadCommentIDSet(new Set([commentId]), 'add');
				}
			} else if (isReply) {
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					if (addCommentInCache && commentId && contentId) {
						const comment = await fetchCommentById({
							commentId,
							contentId,
							fetchPolicy: 'network-only',
						});
						if (comment) {
							addCommentInCache({ newComment: comment });
						}
					}
				} else {
					void handleAddedReplyInCommentThread({
						contentId,
						markerRef,
						commentId,
						addReplyToCommentThread,
						commentType: CommentType.INLINE,
						storedCommentData: lastestStoredCommentData,
						setStoredCommentData,
					});
				}

				if (updateUnreadCommentIDSet) {
					updateUnreadCommentIDSet(new Set([commentId]), 'add');
				}
			}

			break;

		// just for replies
		case InlineCommentSubscriptionEvents.EditorInlineCommentCreated:
		case InlineCommentPubSubEvents.EditorInlineCommentCreated:
			if (isReply) {
				if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					if (addCommentInCache && commentId && contentId) {
						const comment = await fetchCommentById({
							commentId,
							contentId,
							fetchPolicy: 'network-only',
						});
						if (comment) {
							addCommentInCache({ newComment: comment });
						}
					}
				} else {
					void handleAddedReplyInCommentThread({
						contentId,
						markerRef,
						commentId,
						addReplyToCommentThread,
						commentType: CommentType.INLINE,
						storedCommentData: lastestStoredCommentData,
						setStoredCommentData,
					});
				}

				// Check if the annotation exists in the renderer DOM before updating unread comments
				const annotationElement = document.getElementById(markerRef);
				if (annotationElement && updateUnreadCommentIDSet) {
					updateUnreadCommentIDSet(new Set([commentId]), 'add');
				}
			}
			break;
		case InlineCommentSubscriptionEvents.InlineCommentDeleted:
		case InlineCommentPubSubEvents.InlineCommentDeleted:
			// Always handle annotation logic
			if (inlineCommentType === 'TOP_LEVEL') {
				applyStepAndRemoveCommentFromDoc({
					inlineCommentRequestBody,
					rendererActions,
					updateDocument,
					eventEmitter,
				});
			} else {
				if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					refreshCommentThreadIfOpen({
						contentId,
						inlineCommentRequestBody,
					});
				}
			}

			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				if (deleteCommentInCache && commentId) {
					deleteCommentInCache({ commentId });
				}
			}

			// both a parent comment and reply can be deleted
			updateRemovedCommentIdsMap &&
				updateRemovedCommentIdsMap({
					threadKey: markerRef,
					commentId,
					commentActionType: CommentActionType.DELETE_COMMENT,
					isReply,
					commentType: CommentType.INLINE,
				});

			if (updateUnreadCommentIDSet) {
				updateUnreadCommentIDSet(new Set([commentId]), 'remove');
			}

			break;
		case InlineCommentSubscriptionEvents.InlineCommentReattached:
		case InlineCommentPubSubEvents.InlineCommentReattached:
			applyStepAndAddCommentToDoc({
				inlineCommentRequestBody,
				rendererActions,
				updateDocument,
				needsContentUpdate,
				setContentNeedsUpdate,
				publishedDocumentVersion,
				createAnalyticsEvent,
			});
			break;
		case InlineCommentSubscriptionEvents.InlineCommentUpdated:
		case InlineCommentPubSubEvents.InlineCommentUpdated:
			// If the initial comment thread is open, refresh it
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				if (commentId && contentId) {
					// fetchCommentById implicitly updates useAllCommentsDataQuery cache
					void fetchCommentById({ commentId, contentId, fetchPolicy: 'network-only' });
				}
			} else {
				updateCommentBody &&
					void handleEditedComment({
						contentId,
						markerRef,
						commentId,
						updateCommentBody,
					});
			}
			break;
		default:
			return;
	}

	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		if (
			inlineCommentType !== 'REPLY' &&
			!needsContentUpdate &&
			event !== InlineCommentSubscriptionEvents.RendererInlineCommentCreated &&
			event !== InlineCommentSubscriptionEvents.InlineCommentReattached &&
			event !== InlineCommentSubscriptionEvents.InlineCommentDeleted
		) {
			if (rendererActions && contentId && updateUnresolvedInlineComment) {
				void updateRendererAnnotations({
					rendererActions,
					contentId: contentId!,
					updateUnresolvedInlineComment,
				});
			}
		}
	} else {
		// Always update if we've handled the event
		if (
			inlineCommentType !== 'REPLY' &&
			!needsContentUpdate &&
			event !== InlineCommentPubSubEvents.RendererInlineCommentCreated &&
			event !== InlineCommentPubSubEvents.InlineCommentReattached &&
			event !== InlineCommentPubSubEvents.InlineCommentDeleted
		) {
			if (rendererActions && contentId && updateUnresolvedInlineComment) {
				void updateRendererAnnotations({
					rendererActions,
					contentId: contentId!,
					updateUnresolvedInlineComment,
				});
			}
		}
	}
};

export function setUpRendererInlineCommentsPubSub({
	contentId,
	currentUserId,
	contentType,
	rendererActions,
	updateDocument,
	updateUnresolvedInlineComment,
	setContentNeedsUpdate,
	needsContentUpdate,
	setPublishedDocumentVersion,
	publishedDocumentVersion,
	createAnalyticsEvent,
	addReplyToCommentThread,
	addNewCommentThreads,
	updateRemovedCommentIdsMap,
	reopenParentComment,
	updateCommentBody,
	storedCommentData,
	setStoredCommentData,
	addSubscriber,
	setContentId,
	setContentType,
	updateUnreadCommentIDSet,
	addCommentInCache,
	deleteCommentInCache,
	setActiveHighlight,
	setOrderedActiveAnnotationIdList,
	orderedActiveAnnotationIdList,
}: RendererInlineCommentsPubSubFn) {
	const pubSubResourceType = contentType || 'page';
	setCommentPublicationEventParams({
		contentId,
		currentUserId,
		rendererActions,
		updateDocument,
		updateUnresolvedInlineComment,
		needsContentUpdate,
		setContentNeedsUpdate,
		publishedDocumentVersion,
		createAnalyticsEvent,
		addReplyToCommentThread,
		addNewCommentThreads,
		updateRemovedCommentIdsMap,
		reopenParentComment,
		updateCommentBody,
		storedCommentData,
		setStoredCommentData,
		updateUnreadCommentIDSet,
		addCommentInCache,
		deleteCommentInCache,
		setActiveHighlight,
		setOrderedActiveAnnotationIdList,
		orderedActiveAnnotationIdList,
	});
	setPagePublicationEventParams({
		currentUserId,
		setContentNeedsUpdate,
		publishedDocumentVersion,
		setPublishedDocumentVersion,
	});

	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		if (!addSubscriber || !setContentId || !setContentType) {
			return;
		}
		const subscriptions = [];
		setContentType(pubSubResourceType);
		setContentId(contentId);
		const subscribers = [
			{
				subscriberId: 'renderInlineCommentsPubSubInlineCommentResolved',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'inlineCommentResolved',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubInlineCommentUnresolved',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'inlineCommentUnresolved',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubRendererInlineCommentCreated',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'rendererInlineCommentCreated',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubEditorInlineCommentCreated',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'editorInlineCommentCreated',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubInlineCommentDeleted',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'inlineCommentDeleted',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubInlineCommentReattached',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'inlineCommentReattached',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubInlineCommentUpdated',
				onSubscriptionData: onCommentPublicationEvent,
				delta: 'inlineCommentUpdated',
			},
			{
				subscriberId: 'renderInlineCommentsPubSubPageUpdated',
				onSubscriptionData: onPagePublicationEvent,
				delta: 'pageUpdated',
			},
		];

		for (const subscriber of subscribers) {
			subscriptions.push({ subscriberId: addSubscriber(subscriber), delta: subscriber.delta });
		}

		return subscriptions;
	} else {
		void getPubSubClient().then((client) => {
			client
				.joinChannel(pubSubResourceType, contentId)
				.finally(() => {
					void client.subscribe(eventToAvi('resolved:inline_comment'), onCommentPublicationEvent);

					void client.subscribe(eventToAvi('unresolved:inline_comment'), onCommentPublicationEvent);

					void client.subscribe(
						eventToAvi('created:renderer_inline_comment'),
						onCommentPublicationEvent,
					);

					void client.subscribe(
						eventToAvi('created:editor_inline_comment'),
						onCommentPublicationEvent,
					);

					void client.subscribe(eventToAvi('deleted:inline_comment'), onCommentPublicationEvent);

					void client.subscribe(eventToAvi('reattach:inline_comment'), onCommentPublicationEvent);

					void client.subscribe(eventToAvi('updated:inline_comment'), onCommentPublicationEvent);

					void client.subscribe(eventToAvi('publish:page'), onPagePublicationEvent);

					void client.subscribe(eventToAvi('publish:blogpost'), onPagePublicationEvent);
				})
				.catch((error) => {
					throw new Error(`PubSub Web Client joinChannel ${JSON.stringify(error)}`);
				});
		});
	}
}

export function removeRendererInlineCommentsPubSub({
	contentId,
	contentType,
	subscriptions,
	removeSubscriber,
}: RendererInlineCommentsPubUnsubFn) {
	const pubSubResourceType = contentType || 'page';
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning
	if (fg('cc_fps_migration_comments') && fg('confluence_enable_subscriptions-context-provider')) {
		for (const subscriber of subscriptions) {
			removeSubscriber?.(subscriber);
		}
	} else {
		void getPubSubClient().then((client) => {
			void client.leaveChannel(pubSubResourceType, contentId);
			void client.unsubscribe(eventToAvi('resolved:inline_comment'), onCommentPublicationEvent);

			void client.unsubscribe(eventToAvi('unresolved:inline_comment'), onCommentPublicationEvent);

			void client.unsubscribe(
				eventToAvi('created:renderer_inline_comment'),
				onCommentPublicationEvent,
			);

			void client.unsubscribe(
				eventToAvi('created:editor_inline_comment'),
				onCommentPublicationEvent,
			);

			void client.unsubscribe(eventToAvi('deleted:inline_comment'), onCommentPublicationEvent);

			void client.unsubscribe(eventToAvi('reattach:inline_comment'), onCommentPublicationEvent);

			void client.unsubscribe(eventToAvi('updated:inline_comment'), onCommentPublicationEvent);

			void client.unsubscribe(eventToAvi('publish:page'), onPagePublicationEvent);

			void client.unsubscribe(eventToAvi('publish:blogpost'), onPagePublicationEvent);
		});
	}
}
